/**
 * Native shell wiring — everything that only exists when the CRM is running
 * inside the Google Play / App Store build. Every call is a no-op on the web,
 * so index.js can call it unconditionally.
 */
import { Capacitor, registerPlugin } from "@capacitor/core";
import { createLogger, summarizeError } from "../utils/logger";
import { primeNativePushPermission } from "./push";

const log = createLogger("native.shell");

// Over-the-air web updates (@capgo/capacitor-updater — installed in the
// SHELL project, like the biometric plugin; self-hosted, no Capgo account).
// The shell asks OUR server (/appUpdate, see lib/appUpdate/bundle.js) for a
// newer web bundle, downloads it in the background and boots it next time;
// this call is how that bundle proves it came up. Without it the plugin
// assumes the new bundle is broken and rolls back to the previous one after
// appReadyTimeout, so it must run as early as possible. Same registerPlugin
// bridge as biometric.js — no JS dependency on the package in the CRM build.
const CapacitorUpdater = registerPlugin("CapacitorUpdater");

async function notifyOtaBundleReady() {
  try {
    await CapacitorUpdater.notifyAppReady();
  } catch (error) {
    // A shell built before the updater was added has no such plugin; that store
    // build only ever runs its baked-in bundle, so there is nothing to confirm.
    log.warn("native.ota_notify_failed", { ...summarizeError(error) });
  }
}

export const isNativeApp = () => Capacitor.isNativePlatform();
export const nativePlatform = () => Capacitor.getPlatform();

// iOS WebKit zooms the page in when a text field smaller than 16px is focused
// (the theme's inputs are ~13.7px), and leaves it zoomed after the keyboard
// goes away — a lead page opened, a note tapped, and the whole screen stays
// magnified until the user backs out and in. Pinning the scale stops that
// zoom and the double-tap zoom. WKWebView honours these viewport keys (Safari
// ignores them for accessibility), and this only runs in the native shell, so
// the browser/PWA build keeps pinch-zoom exactly as before.
function lockViewportScale() {
  const meta = document.querySelector('meta[name="viewport"]');
  if (!meta) return;
  const content = meta.getAttribute("content") || "";
  if (/maximum-scale/.test(content)) return;
  meta.setAttribute(
    "content",
    `${content}, maximum-scale=1, user-scalable=no`,
  );
}

/**
 * Status bar painted to match the app header, splash screen dismissed once
 * React has mounted, and the Android hardware back button wired to browser
 * history (without it, back closes the app from any screen). Viewport scale
 * locked so a focused input cannot leave the page zoomed in.
 */
export async function initNativeShell() {
  if (!Capacitor.isNativePlatform()) return;

  lockViewportScale();
  // Not awaited: the ready signal must not wait on the plugin imports below.
  notifyOtaBundleReady();
  // Warm the cached notification-permission answer (iOS store app) so the
  // Settings toggle and the first-open sheet render from a real value.
  primeNativePushPermission();

  try {
    const [{ App }, { StatusBar, Style }, { SplashScreen }] = await Promise.all([
      import("@capacitor/app"),
      import("@capacitor/status-bar"),
      import("@capacitor/splash-screen"),
    ]);

    // Light glyphs on the dark brand bar.
    await StatusBar.setStyle({ style: Style.Dark }).catch(() => {});
    if (Capacitor.getPlatform() === "android") {
      await StatusBar.setBackgroundColor({ color: "#1a2a27" }).catch(() => {});
    }

    await App.addListener("backButton", ({ canGoBack }) => {
      if (canGoBack || window.history.length > 1) {
        window.history.back();
      } else {
        App.exitApp();
      }
    });

    await SplashScreen.hide().catch(() => {});
  } catch (error) {
    // A shell that fails to configure must never stop the CRM from rendering.
    log.error("native.init_failed", { ...summarizeError(error) });
  }
}

export default initNativeShell;
